import { createContext, RefObject } from 'react';
import { Canvas, FabricObject } from 'fabric';
import { CanvasJSON } from './types';
import { Layer } from '../layers/hooks/use-layer-controls';
import type { PieceModel } from '../model/types';
import type { SobreimpresoCreateConfig } from '../text/sobreimpreso/types';

export type ActiveTool = 'select' | 'pan';

export type LayerLabelResolver = (object: FabricObject) => string | undefined;

export type EditorCanvasContextValue = {
  // Canvas
  canvas: Canvas | null;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  canvasContainerRef: RefObject<HTMLDivElement | null>;

  // Selection
  selectedObject: FabricObject | null;

  // Actions
  addRectangle: () => void;
  addCircle: () => void;
  addText: () => void;
  addPill: () => void;
  addSvg: (svgText: string) => Promise<void>;
  addSobreimpreso: (config?: Omit<SobreimpresoCreateConfig, 'left' | 'top'>) => void;
  addAutoFitText: () => void;
  deleteSelected: () => void;
  clearCanvas: () => void;

  // Active tool
  activeTool: ActiveTool;
  setActiveTool: (tool: ActiveTool) => void;

  // Zoom
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  fitToContainer: () => void;

  // Clipping
  clipToCanvas: boolean;
  setClipToCanvas: (v: boolean) => void;

  // History
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;

  // Groups multiple canvas updates into a single history entry
  transaction: (fn: () => void | Promise<void>) => Promise<void>;

  // Loads a fully resolved canvas JSON snapshot (a template)
  loadProject: (canvasJSON: CanvasJSON) => Promise<void>;

  // Opens a saved piece straight from its model
  loadPiece: (model: PieceModel) => Promise<void>;

  layoutVersion: number;

  // Layers
  layers: Layer[];
  bringForward: (obj: FabricObject) => void;
  sendBackward: (obj: FabricObject) => void;
  moveNextTo: (obj: FabricObject, targetObj: FabricObject) => void;
  canBringForward: (obj: FabricObject) => boolean;
  canSendBackward: (obj: FabricObject) => boolean;
  focusLayer: (obj: FabricObject) => void;
  toggleLayerInSelection: (obj: FabricObject) => void;

  // Overrides the default layer label generation
  setLayerLabelResolver: (resolver: LayerLabelResolver | null) => void;
};

export const EditorCanvasContext = createContext<EditorCanvasContextValue | null>(null);
